import React, { useState, useRef, useEffect } from 'react';

// Convert URLs in text to clickable links
const renderTextWithLinks = (text) => {
  const urlRegex =
    /(https?:\/\/[^\s]+|www\.[^\s]+|[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}(?:\/[^\s]*)?)/g;
  const parts = text.split(urlRegex);
  const testRegex =
    /^(https?:\/\/[^\s]+|www\.[^\s]+|[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}(?:\/[^\s]*)?)$/;
  return parts.map((part, index) => {
    if (part && testRegex.test(part)) {
      let href = part;
      if (!part.startsWith('http://') && !part.startsWith('https://')) {
        href = `https://${part}`;
      }
      return (
        <a
          key={index}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-400 hover:text-blue-300 underline break-all cursor-pointer"
          onClick={(e) => e.stopPropagation()}
        >
          {part}
        </a>
      );
    }
    return part;
  });
};

const WaitingRoomChat = ({ socket, meetingId, odId, odName, isOpen, onToggle }) => {
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);
  // Tracks how much of the layout viewport is hidden by the on-screen
  // keyboard.  `position: fixed` anchors to the layout viewport (full screen
  // including the area behind the keyboard) on mobile, so without this offset
  // the input row sits under the keyboard and the user can't see what they're
  // typing.  The VisualViewport API reports the keyboard's vertical
  // occlusion as (window.innerHeight - visualViewport.height).
  const [keyboardOffset, setKeyboardOffset] = useState(0);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Track the visual viewport so we can lift the chat panel above the
  // mobile keyboard.  Only updates when the panel is open to avoid running
  // listeners while it's hidden.
  useEffect(() => {
    if (!isOpen) return;
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => {
      const occluded = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      setKeyboardOffset(occluded);
    };
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
      setKeyboardOffset(0);
    };
  }, [isOpen]);

  // Listen for chat messages from the socket
  useEffect(() => {
    if (!socket) return;

    const handleChat = (data) => {
      setMessages((prev) => {
        // Deduplicate by message id
        if (prev.some((m) => m.id === data.id)) return prev;
        return [...prev, data];
      });
      if (!isOpen) {
        setUnreadCount((c) => c + 1);
      }
    };

    socket.on('waiting-room-chat', handleChat);
    return () => {
      socket.off('waiting-room-chat', handleChat);
    };
  }, [socket, isOpen]);

  // Clear unread when opened
  useEffect(() => {
    if (isOpen) setUnreadCount(0);
  }, [isOpen]);

  // Auto-scroll to bottom on new message
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const sendMessage = () => {
    const text = inputText.trim();
    if (!text || !socket) return;

    socket.emit('waiting-room-chat-to-host', {
      meetingId,
      odId,
      odName,
      message: text,
    });
    setInputText('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Floating Chat Icon Button */}
      {!isOpen && (
        <button
          onClick={onToggle}
          className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-gradient-to-r from-[#6a1fbf] to-[#8e01da] rounded-full shadow-lg flex items-center justify-center hover:scale-105 transition-transform"
          title="Chat with host"
        >
          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-[11px] text-white flex items-center justify-center font-bold">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
      )}

      {/* Chat Window — when the mobile keyboard is open, lift the panel by
          the keyboard's height so the input row stays visible above it.
          On wide screens (no keyboard), keyboardOffset is 0 and we keep the
          original 24px bottom margin.  Cap the panel height to the visible
          viewport minus a small margin so the header isn't cut off when the
          keyboard takes up most of the screen. */}
      {isOpen && (
        <div
          className="fixed right-6 z-50 w-80 sm:w-96 bg-[#1a1a2e] border border-white/10 rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-slideIn"
          style={{
            bottom: `${keyboardOffset + 24}px`,
            height: `min(28rem, calc(100dvh - ${keyboardOffset + 48}px))`,
          }}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-[#6a1fbf] to-[#8e01da] px-4 py-3 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              <span className="text-white font-semibold text-sm">Chat with Host</span>
            </div>
            <button
              onClick={onToggle}
              className="text-white/70 hover:text-white transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
            {messages.length === 0 && (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <svg className="w-10 h-10 text-white/20 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                <p className="text-white/40 text-sm">Send a message to the host</p>
                <p className="text-white/25 text-xs mt-1">They'll see it right away</p>
              </div>
            )}
            {messages.map((msg) => {
              const isOwn = msg.senderRole === 'participant' && msg.senderId === odId;
              return (
                <div key={msg.id} className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[75%] rounded-xl px-3 py-2 ${
                    isOwn
                      ? 'bg-gradient-to-r from-[#6a1fbf] to-[#8e01da] text-white'
                      : 'bg-white/10 text-white'
                  }`}>
                    {!isOwn && (
                      <p className="text-[11px] font-medium text-purple-300 mb-0.5">
                        {msg.senderName}
                      </p>
                    )}
                    <p className="text-sm break-words">{renderTextWithLinks(msg.message)}</p>
                    <p className={`text-[10px] mt-1 ${isOwn ? 'text-white/60' : 'text-white/40'}`}>
                      {new Date(msg.sentAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="px-3 py-3 border-t border-white/10 shrink-0">
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                type="text"
                name="rlink-chat-message"
                autoComplete="off"
                data-form-type="other"
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type a message..."
                className="flex-1 bg-white/10 border border-white/10 rounded-xl px-3 py-2 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#6a1fbf]/50 transition-colors"
              />
              <button
                onClick={sendMessage}
                disabled={!inputText.trim()}
                className="p-2 bg-gradient-to-r from-[#6a1fbf] to-[#8e01da] rounded-xl text-white hover:opacity-90 transition-opacity disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default WaitingRoomChat;
